import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
} from "@mui/material";

import { Doughnut } from "react-chartjs-2";
import { Chart, ArcElement, Tooltip, Legend } from "chart.js";
import moment from "moment-timezone";
import { FaArrowLeft } from "react-icons/fa";

Chart.register(ArcElement, Tooltip, Legend);

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const DriverDetails = () => {
  const { driverId } = useParams();
  const navigate = useNavigate();

  const [driver, setDriver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tabValue, setTabValue] = useState(0);
  const [journeys, setJourneys] = useState([]);
  const [restPeriods, setRestPeriods] = useState([]);

  useEffect(() => {
    const fetchDriverData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");

        const driverResponse = await axios.get(
          `${process.env.REACT_APP_BASE_API_URL}/api/drivers/${driverId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const journeysResponse = await axios.get(
          `${process.env.REACT_APP_BASE_API_URL}/api/drivers/${driverId}/journeys`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const restResponse = await axios.get(
          `${process.env.REACT_APP_BASE_API_URL}/api/drivers/${driverId}/rest-periods`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setDriver(driverResponse.data);
        setJourneys(journeysResponse.data);
        setRestPeriods(restResponse.data);
        setError("");
      } catch (err) {
        setError(err.response?.data?.error || "Failed to load driver data");
      } finally {
        setLoading(false);
      }
    };

    fetchDriverData();
  }, [driverId]);

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    return moment(dateString).tz("Asia/Muscat").format("DD/MM/YYYY");
  };

  const formatRestDate = (dateStr) => {
    if (
      !dateStr ||
      dateStr.startsWith("1900-01-01") ||
      dateStr === "1900-01-01"
    ) {
      return "No rest recorded";
    }
    return moment(dateStr).tz("Asia/Muscat").format("DD/MM/YYYY");
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!driver) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        Driver not found
      </Alert>
    );
  }

  // Chart data for work/rest status visualization
  const statusData = {
    labels: ["Work Days", "Remaining Days"],
    datasets: [
      {
        data: [
          driver.consecutive_work_days,
          Math.max(0, 6 - driver.consecutive_work_days),
        ],
        backgroundColor: [driver.needs_rest ? "#f44336" : "#4caf50", "#e0e0e0"],
        borderWidth: 1,
      },
    ],
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 3,
        }}
      >
        <button
          className="bt-back-btn"
          onClick={() => navigate(-1)}
          title="Back"
        >
          <FaArrowLeft />
        </button>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: "#1e293b",
            fontSize: "1.25rem",
            letterSpacing: "-0.01em",
          }}
        >
          <span style={{ color: "#2563eb", fontWeight: 700 }}>
            {driver.driver_name}
          </span>{" "}
          — Journey Details
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Driver Information Card */}
        <Grid item xs={12} md={8}>
          <Card elevation={3} sx={{ minHeight: 180, maxHeight: 260 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Driver Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Driver Code:
                  </Typography>
                  <Typography>
                    {driver.reporting_driver_code ?? "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Status:
                  </Typography>
                  <Typography
                    color={driver.needs_rest ? "error" : "primary"}
                    fontWeight="bold"
                  >
                    {driver.rest_status ?? "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Consecutive Work Days:
                  </Typography>
                  <Typography>
                    {driver.consecutive_work_days ?? "N/A"} / 6
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Last Rest Date:
                  </Typography>
                  <Typography>
                    {formatRestDate(driver.last_rest_date)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Active Journeys:
                  </Typography>
                  <Typography>{driver.active_journeys ?? 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Assigned Trips:
                  </Typography>
                  <Typography>{driver.assigned_trips ?? 0}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Status Visualization */}
        <Grid item xs={12} md={4}>
          <Card elevation={3} sx={{ minHeight: 120, maxHeight: 260 }}>
            <CardContent>
              <Typography variant="h7" gutterBottom>
                Work / Rest Status
              </Typography>
              <Box sx={{ height: "180px" }}>
                <Doughnut
                  data={statusData}
                  options={{
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { position: "bottom" },
                      tooltip: {
                        callbacks: {
                          label: (context) =>
                            `${context.label}: ${context.raw} day(s)`,
                        },
                      },
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs for Journeys & Rest History */}
      <Tabs value={tabValue} onChange={handleTabChange}>
        <Tab label="Recent Journeys" />
        <Tab label="Rest History" />
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        <Paper
          elevation={3}
          sx={{ p: 2, maxHeight: 220, overflowX: "auto", overflowY: "hidden" }}
        >
          <Typography variant="h6" gutterBottom>
            Recent Journeys
          </Typography>
          {journeys.length ? (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                overflowX: "auto",
                maxWidth: "100%",
                minHeight: 100,
                maxHeight: 140,
              }}
            >
              {journeys.slice(0, 10).map((journey) => (
                <Card
                  key={journey.journey_id}
                  sx={{
                    minWidth: 180,
                    maxWidth: 210,
                    flex: "0 0 auto",
                    borderLeft:
                      journey.status === "completed"
                        ? "4px solid #4caf50"
                        : "4px solid #1976d2",
                    background:
                      journey.status === "completed"
                        ? "linear-gradient(135deg, #e0f7fa 0%, #e8f5e9 100%)"
                        : "linear-gradient(135deg, #e3f2fd 0%, #f0f9ff 100%)",
                    p: 1,
                    m: 0,
                  }}
                  elevation={1}
                >
                  <CardContent sx={{ p: 1 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      sx={{ mb: 0.2, color: "#312e81" }}
                    >
                      {journey.journey_plan_no}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color:
                          journey.status === "completed"
                            ? "#388e3c"
                            : "#1976d2",
                        fontWeight: 600,
                        mb: 0.2,
                      }}
                    >
                      {journey.status === "completed"
                        ? "Completed"
                        : "In Progress"}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {formatDateTime(journey.start_date)} -{" "}
                      {journey.end_date
                        ? formatDateTime(journey.end_date)
                        : "Ongoing"}
                    </Typography>
                    {journey.remarks && (
                      <Typography
                        variant="body2"
                        sx={{ mt: 0.5, fontStyle: "italic", color: "#64748b" }}
                      >
                        Remarks: {journey.remarks}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Typography>No recent journeys found.</Typography>
          )}
        </Paper>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Paper
          elevation={3}
          sx={{ p: 2, maxHeight: 220, overflowX: "auto", overflowY: "hidden" }}
        >
          <Typography variant="h6" gutterBottom>
            Rest History
          </Typography>
          {restPeriods.length ? (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                overflowX: "auto",
                maxWidth: "100%",
                minHeight: 100,
                maxHeight: 140,
              }}
            >
              {restPeriods.slice(0, 10).map((rest) => (
                <Card
                  key={rest.rest_id}
                  sx={{
                    minWidth: 180,
                    maxWidth: 210,
                    flex: "0 0 auto",
                    borderLeft: rest.is_manual
                      ? "4px solid #9c27b0"
                      : "4px solid #2196f3",
                    background:
                      "linear-gradient(135deg, #f3e8fd 0%, #e3f2fd 100%)",
                    p: 1,
                    m: 0,
                  }}
                  elevation={1}
                >
                  <CardContent sx={{ p: 1 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      sx={{ mb: 0.2, color: "#312e81" }}
                    >
                      {Math.round(rest.duration_hours / 24)} Day(s) Rest
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: rest.is_manual ? "#9c27b0" : "#1976d2",
                        fontWeight: 600,
                        mb: 0.2,
                      }}
                    >
                      {rest.is_manual ? "Manual Entry" : "Auto Entry"}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {formatDateTime(rest.rest_start_date)} -{" "}
                      {rest.rest_end_date
                        ? formatDateTime(rest.rest_end_date)
                        : "Ongoing"}
                    </Typography>
                    {rest.notes && (
                      <Typography
                        variant="body2"
                        sx={{ mt: 0.5, fontStyle: "italic", color: "#64748b" }}
                      >
                        Notes: {rest.notes}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Typography>No rest periods recorded.</Typography>
          )}
        </Paper>
      </TabPanel>
    </Box>
  );
};

export default DriverDetails;
